import type { CSSProperties } from 'react';
import CardShellEcho from '@/components/cards/common/CardShellEcho';
import { useDerivedNetworkContext } from '@/context';
import { Button, Flex, Grid, theme, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ComingSoonImage } from './ComingSoonImage';

const { Title, Text } = Typography;

/**
 * The unavailable zero state.
 * It shows a message that the content performance insights are coming soon.
 * @returns The unavailable zero state.
 */
const UnavailableZeroStateEcho = () => {
  const navigate = useNavigate();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isLargeScreen = Boolean(screens.lg);

  const shellRootStyle: CSSProperties = {
    width: '100%',
    maxWidth: isLargeScreen ? token.screenLG : undefined,
    padding: token.paddingXL,
  };

  const overlineStyle: CSSProperties = {
    color: token.colorTextSecondary,
    fontSize: token.fontSizeSM,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: token.marginLG,
  };

  const sectionStyle: CSSProperties = {
    flexDirection: 'column',
    alignItems: 'center',
    padding: token.paddingXL,
    textAlign: 'center',
    width: '100%',
  };

  const titleStyle: CSSProperties = {
    marginTop: 0,
    marginBottom: token.marginXS,
    color: token.colorPrimary,
  };

  const bodyTextStyle: CSSProperties = {
    display: 'block',
    fontSize: token.fontSize,
  };

  const dividerStyle: CSSProperties = {
    width: '100%',
    height: 1,
    backgroundColor: '#DEDEF2',
    marginTop: token.marginXL,
    marginBottom: token.marginXL,
  };

  return (
    <CardShellEcho type="secondary" style={shellRootStyle}>
      <Flex vertical align="center" style={{ width: '100%' }}>
        <Flex justify="center" style={{ marginBottom: token.marginXL, width: '100%' }}>
          <ComingSoonImage width={300} height={300} />
        </Flex>
        <Flex vertical align="center" style={sectionStyle}>
          <Title level={5} style={titleStyle}>
            Coming soon
          </Title>
          <Text style={overlineStyle}>
            Content performance insights for audio, video and socials
          </Text>
          <Text style={{ ...bodyTextStyle, marginBottom: token.marginXXL }}>
            We're building a comprehensive view of how audiences engage with your content across
            Podcast Players, YouTube, TikTok and Instagram.
          </Text>
          <Text style={{ ...bodyTextStyle, marginBottom: 0 }}>Launching in 2026.</Text>
        </Flex>
        <Flex style={dividerStyle} />
        <Flex vertical align="center" style={sectionStyle}>
          <Title level={5} style={titleStyle}>
            Available now
          </Title>
          <Text style={overlineStyle}>
            Cross-platform audience insights
          </Text>
          <Text style={{ ...bodyTextStyle, marginBottom: token.margin }}>
            Gain a better understanding of who your listeners and viewers are and how they consume
            your content.
          </Text>

          <Button
            type="primary"
            size="large"
            onClick={() => navigate(`/${selectedNetwork.id}/audience/`)}
          >
            Explore the audience tab
          </Button>
        </Flex>
      </Flex>
    </CardShellEcho>
  );
};

export default UnavailableZeroStateEcho;
